
import * as React from "react";
import type { JSX } from "react";
import { Logo } from "@/assets/logo";
import { Divider, Box, Chip, Stack, Typography } from "@mui/material";
import { useColorScheme } from "@mui/material/styles";
import { ArrowSquareOut as ArrowSquareOutIcon } from "@phosphor-icons/react/dist/ssr/ArrowSquareOut";
import { CaretDown as CaretDownIcon } from "@phosphor-icons/react/dist/ssr/CaretDown";
import { CaretRight as CaretRightIcon } from "@phosphor-icons/react/dist/ssr/CaretRight";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";

import type { NavItemConfig } from "@/types/nav";
import type { DashboardNavColor } from "@/types/settings";
import { paths } from "@/paths";
import { isNavItemActive } from "@/lib/is-nav-item-active";
import { usePathname } from "@/hooks/use-pathname";
import { RouterLink } from "@/components/core/link";
import { useUser } from "../../../api/hooks";
import { ColorScheme } from "../../../styles/theme/types";
import { TranslationFunction } from "../../../types/translation";
import { User } from "../../../types/user";
import { hasDeviceFeature, hasFeature } from "../../feature/hasFeature";
import { icons } from "./nav-icons";
import { WorkspacesSwitch } from "./workspaces-switch";
import { useWorkspace } from "./vertical/workspace-context";
import { DeviceNavigationItem, DeviceNavigationResponse, DeviceNavigationStatus, } from "../../../types/navigationsList";
import { ArrowLeftIcon } from "../../../assets/Icons";
import { linearIconNames, type LinearIconName } from "../../../assets/LinearIconNames";
import { LinearIcon } from "../../../assets/LinearIcon";
import { useNavigationList } from "../../../api/navigation-hooks";

const linearIconNameSet: ReadonlySet<string> = new Set(linearIconNames);

function isLinearIconName(value: string | null | undefined): value is LinearIconName {
    return value != null && linearIconNameSet.has(value);
}


type SideNavItemConfig = NavItemConfig & {
    onClickDeviceUuid?: string;
    onBackNavigationClick?: () => void;
    permissionChecked?: boolean;
    defaultFeatureRef?: string;
};

type NavItemIndicator = "expand" | "device" | "none";

const logoColors = {
    dark: {
        blend_in: "light",
        discrete: "light",
        evident: "light",
    },
    light: {
        blend_in: "dark",
        discrete: "dark",
        evident: "light",
    },
} as Record<ColorScheme, Record<DashboardNavColor, "dark" | "light">>;

export interface GeneralSideNavigationProps {
    color?: DashboardNavColor;
    isMobile: boolean;
    onClose?: () => void;
}

type ResolvedNavigationIcon = Pick<
    SideNavItemConfig,
    "icon" | "iconLinear"
>;

function resolveNavigationIcon({ iconKey }: { iconKey: string | null | undefined; }): ResolvedNavigationIcon {
    if (!iconKey) {
        return {};
    }

    if (isLinearIconName(iconKey)) {
        return {
            icon: undefined,
            iconLinear: <LinearIcon name={iconKey} />,
        };
    }

    if (iconKey in icons) {
        return {
            icon: iconKey as NonNullable<SideNavItemConfig["icon"]>,
            iconLinear: undefined,
        };
    }

    return {};
}

function hasActiveDescendant(items: SideNavItemConfig[] | undefined, pathname: string): boolean {
    if (!items?.length) {
        return false;
    }
    return items.some((item) => {
        const isThisItemActive = isNavItemActive({
            disabled: item.disabled,
            external: item.external,
            href: item.href,
            matcher: item.matcher,
            pathname,
        });
        return (
            isThisItemActive ||
            hasActiveDescendant(
                item.items as SideNavItemConfig[] | undefined,
                pathname
            )
        );
    });
}

function renderLabel(title: string | undefined, t: TranslationFunction): string {
    if (!title) {
        return "";
    }
    return title.startsWith("__raw:")
        ? title.replace("__raw:", "")
        : t(title);
}

function getFeatureHref(featureRef: string | null | undefined, deviceUuid: string): string | undefined {
    const featureHrefMap: Record<string, string> = {
        battery: paths.dashboard.device.battery(deviceUuid),
        inverter: paths.dashboard.device.inverter(deviceUuid),
        events: paths.dashboard.device.events(deviceUuid),
        settings: paths.dashboard.device.settings(deviceUuid),
        information: paths.dashboard.device.information(deviceUuid),
        wallbox: paths.dashboard.device.wallbox(deviceUuid),
        connections: paths.dashboard.device.connections(deviceUuid),
        externData: paths.dashboard.device.connections(deviceUuid),
        auxiliary: paths.dashboard.device.auxiliary(deviceUuid),
        auxData: paths.dashboard.device.auxiliary(deviceUuid),
        control: paths.dashboard.device.control(deviceUuid),
        controlData: paths.dashboard.device.control(deviceUuid),
        singleLineDiagram: paths.dashboard.device.singleLineDiagram(deviceUuid),
        cases: paths.dashboard.device.cases(deviceUuid),
        operations: paths.dashboard.device.operations(deviceUuid),

        primaryData_common: paths.dashboard.device.primaryData.general(deviceUuid),
        primaryData_contacts: paths.dashboard.device.primaryData.contact(deviceUuid),

        monthlyReports: `/dashboard/reports/monthly-reports?deviceUuid=${encodeURIComponent(deviceUuid)}`,
    };

    return featureRef
        ? featureHrefMap[featureRef]
        : undefined;
}

function getDynamicNavigationItemHref(item: DeviceNavigationItem, deviceUuid: string): string | undefined {
    if (!item.featureRef) {
        return undefined;
    }

    if (item.featureKind === "Dynamic") {
        return getDynamicFeatureHref(deviceUuid, item.featureRef);
    }

    return getFeatureHref(item.featureRef, deviceUuid);
}

function calculateBadge(item: DeviceNavigationItem): {
    hasBadge: boolean,
    badgeColor: "primary" | "secondary" | "default" | "error" | "info" | "success" | "warning",
    badgeVariant: "standard" | "dot",
} {

    const hasBadge = item.status?.severity !== undefined &&
        item.status?.severity !== null &&
        item.status?.severity !== DeviceNavigationStatus.none &&
        item.status?.severity !== DeviceNavigationStatus.info;

    let badgeColor: "primary" | "secondary" | "default" | "error" | "info" | "success" | "warning" = "info";
    switch (item.status?.severity) {
        case DeviceNavigationStatus.info: { badgeColor = "info"; break; }
        case DeviceNavigationStatus.warning: { badgeColor = "warning"; break; }
        case DeviceNavigationStatus.error: { badgeColor = "error"; break; }
        case DeviceNavigationStatus.none: { badgeColor = "success"; break; }
        default: { badgeColor = "primary"; break; }
    }

    return {
        hasBadge,
        badgeColor,
        badgeVariant: "dot",
    };
}

function getNavItemIndicator({ isBranch, showBackArrow, showDeviceArrow, }: { isBranch: boolean; showBackArrow: boolean; showDeviceArrow: boolean; }): NavItemIndicator {
    if (showBackArrow) {
        return "none";
    }

    if (showDeviceArrow) {
        return "device";
    }

    if (isBranch) {
        return "expand";
    }

    return "none";
}

function canDisplayDynamicNavigationItem(item: DeviceNavigationItem, availableFeatures: ReadonlySet<string>, userData: User): boolean {
    if (item.itemType === "Device") {
        return Boolean(item.deviceUuid) && hasFeature(userData, "device:list:show");
    }

    if (!item.permissionKey) {
        return false;
    }

    const existsForDevice = availableFeatures.has(item.permissionKey);
    const allowedForUser = hasFeature(userData, item.permissionKey as NavItemConfig["feature"]);

    return existsForDevice && allowedForUser;
}

function getDynamicFeatureHref(deviceUuid: string, featureRef: string): string {
    return paths.dashboard.device.featureRendering(deviceUuid, featureRef);
}
function mapDynamicNavigationItem(
    item: DeviceNavigationItem,
    navigation: DeviceNavigationResponse,
    userData: User,
    availableFeatures: ReadonlySet<string>
): SideNavItemConfig | undefined {
    if (item.itemType === "Device" && item.deviceUuid) {
        if (!canDisplayDynamicNavigationItem(item, availableFeatures, userData)) {
            return undefined;
        }

        return {
            key: `device:${item.deviceUuid}`,
            type: "device",
            title: `__raw:${item.title}`,
            visible: true,
            feature: "device:list:show",
            onClickDeviceUuid: item.deviceUuid,
            permissionChecked: true,
            defaultFeatureRef: item.defaultFeatureRef ?? undefined,
        } satisfies SideNavItemConfig;
    }

    if (item.itemType !== "Feature") {
        return undefined;
    }

    const childItems = (item.childrens ?? [])
        .toSorted((left, right) => left.order - right.order)
        .map((child) =>
            mapDynamicNavigationItem(
                child,
                navigation,
                userData,
                availableFeatures
            )
        )
        .filter((child): child is SideNavItemConfig => child !== undefined);

    const canDisplaySelf = canDisplayDynamicNavigationItem(item, availableFeatures, userData);

    // Keep a branch visible when at least one child is allowed, even if the branch itself has no page.
    if (!canDisplaySelf && childItems.length === 0) {
        return undefined;
    }

    if (!item.featureRef) {
        throw new Error(`Invalid feature navigation item: ${item.title}`);
    }

    const hasChildren = childItems.length > 0;
    const badgeState = calculateBadge(item);
    const resolvedIcon = resolveNavigationIcon({ iconKey: item.iconKey });

    return {
        ...resolvedIcon,
        key: `feature:${item.featureRef}`,
        type: "device",
        title: `__raw:${item.title}`,

        href: hasChildren
            ? undefined
            : getDynamicNavigationItemHref(
                item,
                navigation.device.deviceUuid
            ),

        items: hasChildren ? childItems : undefined,
        visible: true,

        feature: (
            item.permissionKey ?? "device:list:show"
        ) as NavItemConfig["feature"],

        permissionChecked: true,

        badgeVisible: badgeState.hasBadge,
        badgeColor: badgeState.badgeColor,
        badgeVariant: badgeState.badgeVariant,
    } satisfies SideNavItemConfig;
}
function mapDynamicNavigationItems(
    navigation: DeviceNavigationResponse,
    userData: User
): SideNavItemConfig[] {
    const availableFeatures = new Set(navigation.features);

    return navigation.items
        .toSorted((left, right) => left.order - right.order)
        .map((item) =>
            mapDynamicNavigationItem(
                item,
                navigation,
                userData,
                availableFeatures
            )
        )
        .filter((item): item is SideNavItemConfig => item !== undefined)
        .filter((item) => Boolean(item.onClickDeviceUuid || item.href || item.items?.length));
}

export interface DynamicDeviceSideNavigationProps {
    color?: DashboardNavColor;
    isMobile: boolean;
    navigation: DeviceNavigationResponse;
    onClose?: () => void;
    items?: NavItemConfig[];
}
export function DynamicDeviceSideNavigation({ color = "evident", isMobile, items = [], navigation, onClose, }: DynamicDeviceSideNavigationProps): React.JSX.Element {
    const location = useLocation();
    const pathname = location.pathname + location.search;
    const { t } = useTranslation();
    const { colorScheme = "light" } = useColorScheme();

    const navigate = useNavigate();

    const { data: userData, isLoading: isUserLoading, } = useUser();

    const parentDeviceUuid = navigation.device.parentDeviceUuid;

    const { data: parentNavigation } = useNavigationList(parentDeviceUuid, { enabled: Boolean(parentDeviceUuid) });

    const buildNumber = import.meta.env.VITE_BUILD_NUMBER!;

    const logoColor = logoColors[colorScheme][color];

    const navigationItems = React.useMemo(
        () => (userData ? mapDynamicNavigationItems(navigation, userData) : []),
        [navigation, userData]
    );

    const handleDeviceNavigation = React.useCallback(
        (
            deviceUuid: string,
            defaultFeatureRef?: string
        ): void => {
            const targetPath =
                defaultFeatureRef
                    ? paths.dashboard.device.featureRendering(
                        deviceUuid,
                        defaultFeatureRef
                    )
                    : paths.dashboard.device.details(
                        deviceUuid
                    );

            navigate(targetPath);
        },
        [navigate]
    );

    const handleBackNavigation = React.useCallback((): void => {
        if (!parentDeviceUuid) {
            return;
        }

        const parentDefaultFeatureRef = parentNavigation?.device.defaultFeatureRef;

        const targetPath = parentDefaultFeatureRef
            ? paths.dashboard.device.featureRendering(
                parentDeviceUuid,
                parentDefaultFeatureRef
            )
            : paths.dashboard.device.details(parentDeviceUuid);

        navigate(targetPath);
    }, [
        navigate,
        parentDeviceUuid,
        parentNavigation?.device.defaultFeatureRef,
    ]);

    const dynamicNavItems = React.useMemo<NavItemConfig[]>(
        () =>
            items.map((section) => {
                if (!section.items) {
                    return section;
                }

                return {
                    ...section,
                    items: section.items.map((item) => {
                        if (item.key !== "currentDeviceSub") {
                            return item;
                        }

                        return {
                            ...item,
                            title: `__raw:${navigation.device.displayName}`,
                            visible: true,
                            disabled: false,
                            forceOpen: true,
                            items: navigationItems,
                            onBackNavigationClick: navigation.device.parentDeviceUuid ? handleBackNavigation : undefined,
                        } satisfies SideNavItemConfig;
                    }),
                };
            }),
        [items, navigation.device.displayName, navigation.device.parentDeviceUuid, navigationItems, handleBackNavigation]
    );

    if (isUserLoading || !userData) {
        return <></>;
    }

    return (
        <>
            <Stack spacing={2} sx={{ p: 2 }}>
                <Box
                    sx={{
                        position: "relative",
                        height: 64,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >
                    <Box
                        component={RouterLink}
                        href={paths.home}
                        sx={{
                            textDecoration: "none",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                        }}
                    >
                        <Logo color={logoColor} height={32} width={122} />
                    </Box>
                </Box>

                <Divider sx={{ borderColor: "text.secondary", color: "text.secondary" }} />
                <WorkspacesSwitch />
            </Stack>

            <Box
                component="nav"
                sx={{
                    flex: "1 1 auto",
                    overflowY: "auto",
                    p: 2,
                    scrollbarWidth: "none",
                    "&::-webkit-scrollbar": { display: "none" },
                }}
            >
                {renderNavGroups(
                    dynamicNavItems,
                    pathname,
                    t,
                    isMobile,
                    userData,
                    [],
                    String(navigation.device.deviceType),
                    handleDeviceNavigation,
                    onClose
                )}
            </Box>

            <Typography variant="overline" fontSize={7} textAlign="right" sx={{ pr: 1 }}>
                {buildNumber}
            </Typography>
        </>
    );
}

export function GeneralSideNavigation({ color = "evident", isMobile, onClose, }: GeneralSideNavigationProps): React.JSX.Element {
    const { config } = useWorkspace();
    const pathname = usePathname();
    const { t } = useTranslation();
    const { colorScheme = "light" } = useColorScheme();

    const buildNumber = import.meta.env.VITE_BUILD_NUMBER!;
    const logoColor = logoColors[colorScheme][color];

    const { data: userData, isLoading: isUserLoading, } = useUser();

    const navItems = React.useMemo<NavItemConfig[]>(
        () => config.navItems.map((section: { items: any[]; }) => {
            if (!section.items) {
                return section;
            }

            return {
                ...section,
                items: section.items.map((item) =>
                    item.key === "currentDeviceSub"
                        ? { ...item, visible: false, disabled: true }
                        : item
                ),
            };
        }),
        [config.navItems]
    );

    const handleDeviceNavigation = React.useCallback((): void => undefined, []);

    if (isUserLoading || !userData) {
        return <></>;
    }

    return (
        <>
            <Stack spacing={2} sx={{ p: 2 }}>
                <Box
                    sx={{
                        position: "relative",
                        height: 64,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >
                    <Box
                        component={RouterLink}
                        href={paths.home}
                        sx={{
                            textDecoration: "none",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                        }}
                    >
                        <Logo color={logoColor} height={32} width={122} />
                    </Box>
                </Box>
                <Divider sx={{ borderColor: "text.secondary", color: "text.secondary" }} />
                <WorkspacesSwitch />
            </Stack>

            <Box
                component="nav"
                sx={{
                    flex: "1 1 auto",
                    overflowY: "auto",
                    p: 2,
                    scrollbarWidth: "none",
                    "&::-webkit-scrollbar": { display: "none" },
                }}
            >
                {renderNavGroups(
                    navItems,
                    pathname,
                    t,
                    isMobile,
                    userData,
                    [],
                    "",
                    handleDeviceNavigation,
                    onClose
                )}
            </Box>

            <Typography variant="overline" fontSize={7} textAlign="right" sx={{ pr: 1 }}>
                {buildNumber}
            </Typography>
        </>
    );
}

function renderNavGroups(
    items: NavItemConfig[],
    pathname: string,
    t: TranslationFunction,
    isMobile: boolean,
    userData: User,
    deviceFeatures: string[],
    deviceType: string,
    onNavigateDevice: (deviceUuid: string, defaultFeatureRef?: string) => void,
    onClose?: () => void
): React.JSX.Element {
    const visibleItems = items.filter((item) => item.visible === undefined || item.visible);

    return (
        <Stack component="ul" spacing={2} sx={{ listStyle: "none", m: 0, p: 0 }}>
            {visibleItems.map((item) => (
                <Stack component="li" key={item.key} spacing={1.5}>
                    <div>
                        {renderNavItems({
                            depth: 0,
                            items: item.items as SideNavItemConfig[],
                            pathname,
                            t,
                            userData,
                            deviceFeatures,
                            deviceType,
                            onNavigateDevice,
                            onClose,
                            isMobile,
                        })}
                    </div>
                </Stack>
            ))}
        </Stack>
    );
}

function renderNavItems({
    depth = 0,
    items = [],
    pathname,
    t,
    userData,
    deviceFeatures,
    deviceType,
    onNavigateDevice,
    onClose,
    isMobile,
}: {
    depth: number;
    items?: SideNavItemConfig[];
    pathname: string;
    t: TranslationFunction;
    userData: User;
    deviceFeatures: string[];
    deviceType: string;
    onNavigateDevice: (deviceUuid: string, defaultFeatureRef?: string) => void;
    onClose?: () => void;
    isMobile: boolean;
}): React.JSX.Element {

    const visibleItems = items.filter((item) => {
        const { items: childItems, key, ...navItem } = item;

        if (navItem.visible === false) {
            return false;
        }

        if (!navItem.permissionChecked) {
            if (!navItem.feature) {
                return false;
            }

            if (!hasFeature(userData, navItem.feature)) {
                return false;
            }
        }

        if (navItem.deviceFeature && navItem.type === "device" && deviceFeatures.length > 0) {
            return hasDeviceFeature(deviceFeatures, deviceType, navItem.deviceFeature);
        }

        if (navItem.deviceFeature && navItem.type === "common" && userData.deviceFeatures?.length) {
            return hasDeviceFeature(userData.deviceFeatures, deviceType, navItem.deviceFeature);
        }

        return true;
    });

    return (
        <Stack component="ul" data-depth={depth} spacing={1} sx={{ listStyle: "none", m: 0, p: 0 }}>
            {visibleItems.map((currentItem) => {
                const {
                    key,
                    title,
                    items: childItems,
                    badgeColor,
                    badgeVisible,
                    badgeValue,
                    badgeVariant,
                    ...item
                } = currentItem;

                const isInPrimaryDataRoute = pathname.includes("/primaryData/");

                const forceOpen =
                    item.forceOpen ??
                    (childItems?.length
                        ? hasActiveDescendant(childItems as SideNavItemConfig[], pathname) ||
                        (key === "devices:primaryData" && isInPrimaryDataRoute)
                        : false);

                return (
                    <NavItem
                        key={key}
                        depth={depth}
                        forceOpen={forceOpen}
                        hasChildren={Boolean(childItems?.length)}
                        pathname={pathname}
                        title={renderLabel(title, t)}
                        isMobile={isMobile}
                        onClose={onClose}
                        badgeVisible={badgeVisible}
                        badgeValue={badgeValue}
                        badgeColor={badgeColor}
                        badgeVariant={badgeVariant}
                        onNavigateDevice={onNavigateDevice}
                        {...item}
                    >
                        {childItems?.length
                            ? renderNavItems({
                                depth: depth + 1,
                                items: childItems as SideNavItemConfig[],
                                pathname,
                                t,
                                userData,
                                deviceFeatures,
                                deviceType,
                                onNavigateDevice,
                                onClose,
                                isMobile,
                            })
                            : null}
                    </NavItem>
                );
            })}
        </Stack>
    );
}

interface NavItemProps extends Omit<SideNavItemConfig, "items"> {
    children?: React.ReactNode;
    hasChildren: boolean;
    depth: number;
    forceOpen?: boolean;
    pathname: string;
    badgeValue?: number;
    iconLinear?: JSX.Element;
    labelColor?: "primary" | "secondary" | "default" | "error" | "info" | "success" | "warning";
    onClose?: () => void;
    isMobile: boolean;
    badgeVisible?: boolean;
    badgeColor?: "primary" | "secondary" | "default" | "error" | "info" | "success" | "warning";
    badgeVariant?: "standard" | "dot";
    onNavigateDevice: (deviceUuid: string, defaultFeatureRef?: string) => void;

}

const NavItem = React.memo(function NavItem({
    children,
    hasChildren,
    depth,
    disabled,
    external,
    forceOpen = false,
    href,
    icon,
    iconLinear,
    label,
    labelColor = "primary",
    matcher,
    pathname,
    title,
    onClose,
    disableOnClose,
    isMobile,
    badgeVisible,
    badgeValue,
    badgeColor = "default",
    badgeVariant = "standard",
    onClickDeviceUuid,
    defaultFeatureRef,
    onNavigateDevice,
    onBackNavigationClick,
}: NavItemProps): React.JSX.Element {
    const [open, setOpen] = React.useState(forceOpen);

    const active = isNavItemActive({ disabled, external, href, matcher, pathname });

    const Icon = icon ? icons[icon] : null;
    const ExpandIcon = open ? CaretDownIcon : CaretRightIcon;

    const isBranch = hasChildren && !href;
    const showChildren = isBranch && open;
    const showDeviceArrow = Boolean(onClickDeviceUuid);
    const showBackArrow = Boolean(onBackNavigationClick);

    const indicator = getNavItemIndicator({ isBranch, showBackArrow, showDeviceArrow });

    React.useEffect(() => {
        setOpen((previous) => (previous === forceOpen ? previous : forceOpen));
    }, [forceOpen]);

    const handleBranchClick = (event: React.MouseEvent<HTMLDivElement>): void => {
        event.stopPropagation();

        if (showBackArrow || disabled) {
            return;
        }

        setOpen((previous) => !previous);
    };

    const handleBranchKeyUp = (event: React.KeyboardEvent<HTMLDivElement>): void => {
        if (event.key !== "Enter" && event.key !== " ") {
            return;
        }

        if (showBackArrow || disabled) {
            return;
        }

        event.preventDefault();
        setOpen((previous) => !previous);
    };

    const handleDeviceClick = (event: React.MouseEvent): void => {
        event.preventDefault();
        event.stopPropagation();

        if (disabled || !onClickDeviceUuid) {
            return;
        }

        onNavigateDevice(onClickDeviceUuid, defaultFeatureRef);
    };

    return (
        <Box
            component="li"
            data-depth={depth}
            sx={{ userSelect: "none" }}
            onClick={() => {
                if (!isBranch && !onClickDeviceUuid && isMobile && onClose && !disableOnClose) {
                    onClose();
                }
            }}
        >
            <Box
                {...(isBranch
                    ? {
                        onClick: handleBranchClick,
                        onKeyUp: handleBranchKeyUp,
                        role: "button",
                    }
                    : href
                        ? {
                            component: external ? "a" : RouterLink,
                            href,
                            target: external ? "_blank" : undefined,
                            rel: external ? "noreferrer" : undefined,
                            onClick: (event: React.MouseEvent): void => {
                                const currentHref = globalThis.location.pathname + globalThis.location.search;

                                if (!external && currentHref === href) {
                                    event.preventDefault();
                                }
                            },
                        }
                        : {
                            role: "button",
                            onClick: handleDeviceClick,
                        })}
                sx={{
                    alignItems: "center",
                    borderRadius: 1,
                    color: "var(--NavItem-color)",
                    cursor: disabled ? "not-allowed" : "pointer",
                    display: "flex",
                    flex: "0 0 auto",
                    gap: 1,
                    p: "6px 16px",
                    position: "relative",
                    textDecoration: "none",
                    whiteSpace: "nowrap",

                    ...(disabled && {
                        bgcolor: "var(--NavItem-disabled-background)",
                        color: "var(--NavItem-disabled-color)",
                    }),

                    ...(active && {
                        bgcolor: "var(--NavItem-active-background)",
                        color: "var(--NavItem-active-color)",

                        ...(depth > 0 && {
                            "&::before": {
                                bgcolor: "var(--NavItem-children-indicator)",
                                borderRadius: "2px",
                                content: '" "',
                                height: "20px",
                                left: "-14px",
                                position: "absolute",
                                width: "3px",
                            },
                        }),
                    }),

                    ...(open && {
                        color: "var(--NavItem-open-color)",
                    }),

                    "&:hover": {
                        ...(!disabled && !active && {
                            bgcolor: "var(--NavItem-hover-background)",
                            color: "var(--NavItem-hover-color)",
                        }),
                    },
                }}
                tabIndex={0}
            >
                {showBackArrow ? (
                    <Box
                        component="button"
                        type="button"
                        aria-label="Back"
                        onClick={(event) => {
                            event.preventDefault();
                            event.stopPropagation();
                            onBackNavigationClick?.();
                        }}
                        sx={{
                            alignItems: "center",
                            background: "transparent",
                            border: 0,
                            color: "inherit",
                            cursor: "pointer",
                            display: "flex",
                            flex: "0 0 auto",
                            justifyContent: "center",
                            p: 0,
                        }}
                    >
                        <ArrowLeftIcon size={18} />
                    </Box>
                ) : null}

                <Box sx={{ alignItems: "center", display: "flex", justifyContent: "center", flex: "0 0 auto" }}>
                    {iconLinear ?? null}

                    {Icon ? (
                        <Icon
                            fill={active ? "var(--NavItem-icon-active-color)" : "var(--NavItem-icon-color)"}
                            fontSize="var(--icon-fontSize-md)"
                            weight={forceOpen || active ? "fill" : undefined}
                        />
                    ) : null}
                </Box>

                <Box sx={{ flex: "1 1 auto" }}>
                    <Typography
                        component="span"
                        sx={{ color: "inherit", fontSize: "0.875rem", fontWeight: 500, lineHeight: "28px" }}
                    >
                        {title}
                    </Typography>
                </Box>

                {label ? <Chip color={labelColor} label={label} size="small" /> : null}

                {badgeVisible ? (
                    badgeVariant === "dot" ? (
                        <Box
                            sx={{
                                width: 8,
                                height: 8,
                                borderRadius: "50%",
                                bgcolor: `${badgeColor}.main`,
                                flexShrink: 0,
                            }}
                        />
                    ) : (
                        <Chip color={badgeColor} label={badgeValue} size="small" />
                    )
                ) : null}

                {external ? (
                    <Box sx={{ alignItems: "center", display: "flex", flex: "0 0 auto" }}>
                        <ArrowSquareOutIcon color="var(--NavItem-icon-color)" fontSize="var(--icon-fontSize-sm)" />
                    </Box>
                ) : null}

                {indicator === "expand" ? (
                    <Box sx={{ alignItems: "center", display: "flex", flex: "0 0 auto" }}>
                        <ExpandIcon color="var(--NavItem-expand-color)" fontSize="var(--icon-fontSize-sm)" />
                    </Box>
                ) : null}

                {indicator === "device" ? (
                    <Box sx={{ alignItems: "center", display: "flex", flex: "0 0 auto" }}>
                        <CaretRightIcon color="var(--NavItem-expand-color)" fontSize="var(--icon-fontSize-sm)" />
                    </Box>
                ) : null}
            </Box>

            {showChildren ? (
                <Box sx={{ pl: "24px" }}>
                    <Box sx={{ borderLeft: "1px solid var(--NavItem-children-border)", pl: "12px" }}>
                        {children}
                    </Box>
                </Box>
            ) : null}
        </Box>
    );
});
